import React, { useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  getHolds,
  addHold,
  updateHold,
  deleteHold,
} from '../../services/SettingsRepository';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import { StorageKey } from '../../../../shared/auth/DataService';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import useRowEditing from '../../../../shared/hooks/useRowEditing';
import getActionsColumn from '../../../../shared/components/datagrid/getActionsColumn';
import AddRowToolbar from '../../../../shared/components/datagrid/AddRowToolbar';
import { scrollToNewRow } from '../../../../shared/utils/scrollToNewRow';

const HoldTable = () => {
  const [holds, setHolds] = useState([]);
  const [nextId, setNextId] = useState(1);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows: holds, setRows: setHolds });

  const fetchHolds = async () => {
    try {
      const holdsResponse = await getHolds();
      setHolds(holdsResponse);
      setNextId(
        holdsResponse.length
          ? Math.max(...holdsResponse.map((h) => h.id)) + 1
          : 1
      );
    } catch (error) {
      console.error('Error fetching holds:', error);
      showSnackbar('Error fetching holds.', 'error');
    }
  };

  useEffect(() => {
    fetchHolds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (id) => async () => {
    try {
      await deleteHold(id);
      await fetchHolds();
      showSnackbar('Hold deleted successfully!');
    } catch (error) {
      console.error('Error deleting hold:', error);
      showSnackbar('Error deleting hold.', 'error');
    }
  };

  // Validate ID for new rows: must be a positive integer and not already in use
  const processRowUpdate = async (newRow) => {
    if (newRow.isNew) {
      const idNum = Number(newRow.id);
      if (
        newRow.id === '' ||
        isNaN(idNum) ||
        idNum < 0 ||
        !Number.isInteger(idNum)
      ) {
        showSnackbar('ID must be a positive whole number.', 'error');
        throw new Error('ID must be a positive whole number.');
      }
      const existing = holds.find((h) => h.id === idNum && !h.isNew);
      if (existing) {
        showSnackbar(`A hold with ID ${idNum} already exists.`, 'error');
        throw new Error(`A hold with ID ${idNum} already exists.`);
      }
    }

    try {
      const emea = localStorage.getItem(StorageKey.userInfo_sub);
      if (!emea) throw new Error('User EMEA not found in localStorage');

      const { id, ...rowWithoutId } = newRow;
      const payload = { ...rowWithoutId, updatedBy: { emea } };

      if (newRow.isNew) {
        await addHold({ ...payload, id: Number(id) });
        showSnackbar('Hold added successfully!');
      } else {
        await updateHold(id, payload);
        showSnackbar('Hold updated successfully!');
      }
      await fetchHolds();
    } catch (error) {
      console.error('Error saving hold:', error);
      showSnackbar('Error saving hold.', 'error');
      if (newRow.isNew) {
        setHolds((prev) => prev.filter((row) => row.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const emea = localStorage.getItem(StorageKey.userInfo_sub);
    if (!emea) {
      showSnackbar('Error fetching user.', 'error');
      return;
    }
    const newId = nextId;
    setNextId(newId + 1);
    setHolds((oldRows) => [
      ...oldRows,
      {
        id: newId,
        name: '',
        shortName: '',
        updatedBy: { emea },
        createdBy: { emea },
        isNew: true,
      },
    ]);
    startEditingRow(newId, 'id');
    // Clear any active filters so the new row is visible, then scroll to it
    apiRef.current?.setFilterModel({ items: [] });
    scrollToNewRow(apiRef, holds.length + 1);
  };

  const holdColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 150,
      editable: true,
      renderEditCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            px: 1,
          }}
        >
          <TextField
            value={params.value != null ? params.value : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'id',
                  value: val === '' ? '' : Number(val),
                });
              }
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { padding: 0 },
            }}
            placeholder='0'
            size='small'
            variant='standard'
            sx={{ flex: 1 }}
          />
        </Box>
      ),
    },
    { field: 'name', headerName: 'Name', width: 300, editable: true },
    {
      field: 'shortName',
      headerName: 'Short Name',
      width: 150,
      editable: true,
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={holds}
        columns={holdColumns}
        getRowId={(row) => row.id}
        editMode='row'
        // ID is editable only for new rows; isCellEditable prevents editing on existing rows
        isCellEditable={(params) => params.field !== 'id' || !!params.row.isNew}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{ toolbar: { label: 'Add Hold', onAdd: handleAddClick } }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: { columnVisibilityModel: { id: true } },
        }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default HoldTable;
